import { Dialog, DialogOptions, Toast, LoadingType, ToastOptions } from 'vant'
import Decimal from 'decimal.js'
import { isAndrod, isAgriApp } from './app'
import AppHost from './app'
import type { AndroidAppHost } from './app'
import { Sha256 } from '@aws-crypto/sha256-js'
interface ToastLoadingParams {
  message?: string
  duration?: number
  loadingType?: LoadingType
}

export function showConfirmModal(option: DialogOptions = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    Dialog.confirm({
      ...option,
    })
      .then(() => {
        resolve()
      })
      .catch(() => {
        reject()
      })
  })
}

export function showToastLoading(options?: ToastLoadingParams): void {
  options = options || {}
  Toast.loading({
    message: options.message || '加载中...',
    forbidClick: true,
    loadingType: options.loadingType || 'spinner',
    duration: options.duration || 0,
  })
}
export function showNormalToast(message: string | ToastOptions): void {
  Toast(message)
}
export function showToastSuccess(message: string): void {
  Toast.success(message)
}
export function showToastFail(message: string): void {
  Toast.fail(message)
}
export function clearToast(): void {
  Toast.clear()
}

export function debouce(fn: Function, time: number, context?: any): Function {
  let timeID = 0
  const wrapFn = function (...args: any[]) {
    clearTimeout(timeID)
    timeID = setTimeout(() => {
      fn.apply(context, args)
    }, time)
  }
  return wrapFn
}

export const getQueryStringArgs = (url: string): { [propName: string]: string } => {
  const arr = url.split('?')
  const qs = arr.length === 2 ? arr[1] : ''
  const args: { [propName: string]: string } = {}
  const items = qs.length ? qs.split('&') : []
  for (let i = 0; i < items.length; i++) {
    const item = items[i].split('=')
    const name = decodeURIComponent(item[0])
    const value = decodeURIComponent(item[1])
    if (name.length) {
      args[name] = value
    }
  }
  return args
}

/**
 * 给数字添加逗号（千分位）
 * @param num 数字
 * @returns {string}
 */
export function toThousands(num = 0): string {
  return num.toString().replace(/\d+/, function (n): string {
    return n.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
  })
}

export function clearCookie(): null {
  const keys = document.cookie.match(/[^ =;]+(?=\=)/g)
  if (keys) {
    for (let i = keys.length; i--; ) {
      document.cookie = keys[i] + '=0;path=/;expires=' + new Date(0).toUTCString() //清除当前域名下的,例如：m.kevis.com
      document.cookie =
        keys[i] + '=0;path=/;domain=' + document.domain + ';expires=' + new Date(0).toUTCString() //清除当前域名下的，例如 .m.kevis.com
      document.cookie = keys[i] + '=0;path=/;domain=kevis.com;expires=' + new Date(0).toUTCString() //清除一级域名下的或指定的，例如 .kevis.com
    }
  }
  console.log('已清除')
  return null
}

// eslint-disable-next-line @typescript-eslint/unbound-method
const toString = Object.prototype.toString

export function is(val: unknown, type: string) {
  return toString.call(val) === `[object ${type}]`
}

export function isDef<T = unknown>(val?: T): val is T {
  return typeof val !== 'undefined'
}

export function isUnDef<T = unknown>(val?: T): val is T {
  return !isDef(val)
}

export function isObject(val: any): val is Record<any, any> {
  return val !== null && is(val, 'Object')
}

export function isEmpty<T = unknown>(val: T): val is T {
  if (isArray(val) || isString(val)) {
    return val.length === 0
  }

  if (val instanceof Map || val instanceof Set) {
    return val.size === 0
  }

  if (isObject(val)) {
    return Object.keys(val).length === 0
  }

  return false
}

export function isDate(val: unknown): val is Date {
  return is(val, 'Date')
}

export function isNull(val: unknown): val is null {
  return val === null
}

export function isNullAndUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) && isNull(val)
}

export function isNullOrUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) || isNull(val)
}

export function isNumber(val: unknown): val is number {
  return is(val, 'Number')
}

export function isPromise<T = any>(val: unknown): val is Promise<T> {
  return is(val, 'Promise') && isObject(val) && isFunction(val.then) && isFunction(val.catch)
}

export function isString(val: unknown): val is string {
  return is(val, 'String')
}

export function isFunction(val: unknown): val is Function {
  return typeof val === 'function'
}

export function isBoolean(val: unknown): val is boolean {
  return is(val, 'Boolean')
}

export function isRegExp(val: unknown): val is RegExp {
  return is(val, 'RegExp')
}

export function isArray(val: any): val is Array<any> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return val && Array.isArray(val)
}

export function isWindow(val: any): val is Window {
  return typeof window !== 'undefined' && is(val, 'Window')
}

export function isElement(val: unknown): val is Element {
  return isObject(val) && !!val.tagName
}

export function isMap(val: unknown): val is Map<any, any> {
  return is(val, 'Map')
}

export const isServer = typeof window === 'undefined'

export const isClient = !isServer

export function isUrl(path: string): boolean {
  const reg =
    /(((^https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)$/
  return reg.test(path)
}

export function clearEmptyProperty<T = any>(obj: any = {}): T {
  if (!obj || !isObject(obj)) {
    return {} as T
  }
  const tem: any = {}
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (!isEmpty(value)) {
      tem[key] = value
    }
  })
  return tem
}

export function base64ToFile(dataUrl: string, fileName = ''): File {
  // base64字符串转file文件
  const arr: string[] = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let len = bstr.length
  const u8arr = new Uint8Array(len)
  while (len--) {
    u8arr[len] = bstr.charCodeAt(len)
  }
  const BlobObj = new Blob([u8arr], { type: mime })
  return new File([BlobObj], fileName || String(Date.now()), {
    type: BlobObj.type,
    lastModified: Date.now(),
  })
}

/**
 * 数据运算类
 */
export function plus(num1: number, num2: number, fixed?: number): number {
  const x = new Decimal(num1)
  const y = x.plus(num2)
  return fixed ? parseFloat(y.toFixed(fixed)) : y.toNumber()
}
//减法
export function minus(num1: number, num2: number, fixed?: number): number {
  const x = new Decimal(num1)
  const y = x.minus(num2)
  return fixed ? parseFloat(y.toFixed(fixed)) : y.toNumber()
}
// 乘法
export function times(num1: number, num2: number, fixed?: number): number {
  const x = new Decimal(num1)
  const y = x.times(num2)
  return fixed ? parseFloat(y.toFixed(fixed)) : y.toNumber()
}
//除法
export function div(num1: number, num2: number, fixed?: number): number {
  const x = new Decimal(num1)
  const y = new Decimal(num2)
  const z = x.div(y)
  return fixed ? parseFloat(z.toFixed(fixed)) : z.toNumber()
}
//加法(多个参数叠加)
export function plusAll(...params: number[]): number {
  let param1 = new Decimal(params[0])
  params.forEach((item: number, index: number) => {
    if (index > 0) {
      param1 = param1.plus(item)
    }
  })
  return param1.toNumber()
}

// 设置农服底部操作栏
export function setNFBottomOperatorBar() {
  const appHost = AppHost as AndroidAppHost
  const android = window.android as AndroidAppHost
  const ishasSetNavBarHandle = android && android.setNavigationBarStatus
  return {
    needFixRoute: ['/malfunction-repair', '/insurance', '/complete-machine-delivery'],
    isCanUse: isAndrod && isAgriApp,
    ishasSetNavBarHandle,
    setBottomHandle(fitsWindow = false, color = '#F5F6F7') {
      if (isAndrod && isAgriApp && ishasSetNavBarHandle) {
        appHost.setNavigationBarStatus(fitsWindow, color)
      }
    },
  }
}

// 字符串加密
export function getSha256ValueSync(value: string) {
  const hash = new Sha256()
  hash.update(value)
  const result = hash.digestSync()
  const hashArray = Array.from(new Uint8Array(result))
  const hashValue = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hashValue
}

// 传入img链接，隐式使用canvas按IMG实际尺寸渲染后，获取base64编码的数据
export async function getImageUrlBase64Data(url: string): Promise<{
  unHeaderData: string
  sourceData: string
}> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // 尝试跨域，如果服务器不支持可能会失败
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('无法获取canvas上下文'))
          return
        }
        ctx.drawImage(img, 0, 0)
        const dataUrl = canvas.toDataURL() // 默认 PNG
        const unHeaderData = dataUrl?.replace(/^data:image\/(png|jpg);base64,/, '') || ''
        resolve({
          unHeaderData,
          sourceData: dataUrl,
        })
      } catch (error) {
        reject(error)
      }
    }
    img.onerror = (err) => {
      reject(new Error(`图片加载失败: ${err}`))
    }
    img.src = url
    // 如果图片已经缓存且设置了crossOrigin，可能会立即触发onload，但需要确保跨域不污染canvas
  })
}
